import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Input, Modal, Progress, Select, Space, Tag, Tooltip, Typography, message } from 'antd'
import { CloseCircleFilled, FolderOpenOutlined, InboxOutlined, PictureOutlined } from '@ant-design/icons'
import type { UploadConfig } from '../../../api/client'
import type { UploadModalPlacement } from '../../../components/UploadModal'
import { workspaceImagesBaseUrl } from '../../api'
import { formatBytes, scanFiles, supportsDirectoryPicker, type ScanResult, type SkipReason } from './folderScan'
import { imageUploadManager, useWorkspaceUploadBatches } from './imageUploadManager'
import { resolveUploadLimits } from './uploadLimits'
import type { BatchPlacement, UploadBatchView } from './uploadManager'

const FAILED_ROWS_CAP = 200

/**
 * Image Asset Center upload dialog (VistoeraTASK-00522): pick images OR a whole
 * folder, review it locally (counts / size / target / skipped), then start.
 *
 * The dialog owns NO upload state: starting a batch hands it to the global
 * `imageUploadManager`, so closing the dialog (or leaving the page section)
 * never stops anything and re-opening shows the live progress.
 */
export default function ImageAssetUploadModal({
  open,
  onClose,
  workspaceId,
  config,
  placement,
  title,
  autoProcess = false,
  assetSource = 'browser_upload',
}: {
  open: boolean
  onClose: () => void
  workspaceId: string
  config: UploadConfig | null
  placement?: UploadModalPlacement
  title?: string
  autoProcess?: boolean
  assetSource?: string
}) {
  const { t } = useTranslation('imageAssets')
  const limits = useMemo(() => resolveUploadLimits(config), [config])
  const batches = useWorkspaceUploadBatches(workspaceId)
  const [pending, setPending] = useState<ScanResult<File> | null>(null)
  const [collectionId, setCollectionId] = useState<string>('')
  const [visibility, setVisibility] = useState<'workspace' | 'private'>('workspace')
  const [newCollectionName, setNewCollectionName] = useState('')
  const [creatingCollection, setCreatingCollection] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const filesInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const folderSupported = useMemo(() => supportsDirectoryPicker(), [])

  const maxFileLabel = formatBytes(limits.maxFileBytes)
  const maxTotalLabel = formatBytes(limits.maxTotalBytes)
  const typesLabel = useMemo(
    () => [...limits.allowedMime].map((m) => m.replace('image/', '').toUpperCase().replace('JPEG', 'JPG')).join(' / '),
    [limits],
  )

  // Default target = the library currently open (or 未整理图片); reset on open.
  useEffect(() => {
    if (!open) {
      setPending(null)
      return
    }
    if (placement) {
      setCollectionId(placement.defaultCollectionId || placement.unsortedId || '')
      setVisibility('workspace')
      setNewCollectionName('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const targetName = useMemo(() => {
    if (!placement) return t('uploadBulk.targetPersonal')
    const found = placement.collections.find((c) => c.id === collectionId)
    return found?.name ?? ''
  }, [placement, collectionId, t])

  const currentPlacement = (): BatchPlacement => ({
    collectionId: placement && collectionId && collectionId !== placement.unsortedId ? collectionId : null,
    collectionName: placement ? targetName : null,
    visibility: placement ? visibility : null,
  })

  const startBatch = (scan: ScanResult<File>, source: 'files' | 'folder') => {
    if (scan.blocked || scan.valid.length === 0) return
    imageUploadManager.enqueue({
      workspaceId,
      source,
      label: scan.folderName,
      files: scan.valid.map((v) => ({ file: v.file, mime: v.mime, relativePath: v.relativePath })),
      placement: currentPlacement(),
      autoProcess,
      assetSource,
      directToR2: Boolean(config?.direct_to_r2),
      scopeBaseUrl: workspaceImagesBaseUrl(workspaceId),
      skippedCount: scan.skipped.length,
      limits: {
        concurrency: limits.concurrency,
        retryMaxAttempts: limits.retryMaxAttempts,
        retryBaseDelayMs: limits.retryBaseDelayMs,
        retryMaxDelayMs: limits.retryMaxDelayMs,
      },
    })
    setPending(null)
  }

  const limitErrorText = (scan: ScanResult<File>): string[] =>
    scan.limitErrors.map((e) => {
      if (e.code === 'too_many_files') return t('uploadBulk.overLimitFiles', { count: e.actual, max: e.max })
      if (e.code === 'too_large_total') {
        return t('uploadBulk.overLimitTotal', { size: formatBytes(e.actual), max: formatBytes(e.max) })
      }
      return t('uploadBulk.nothingToUpload')
    })

  const onPickFiles = (list: FileList | File[]) => {
    const scan = scanFiles<File>(Array.from(list), limits)
    if (scan.blocked) {
      message.error({ key: 'iac-upload-limit', content: limitErrorText(scan).join(' ') })
      return
    }
    // Plain multi-file selection keeps its original behaviour: starts at once.
    const skippedTotal = scan.skipped.length
    if (skippedTotal > 0) {
      message.warning({
        key: 'iac-upload-skipped',
        content: (Object.keys(scan.skippedByReason) as SkipReason[])
          .filter((r) => scan.skippedByReason[r] > 0)
          .map((r) => `${t(`uploadBulk.skipReason.${r}`)} × ${scan.skippedByReason[r]}`)
          .join(' · '),
      })
    }
    startBatch(scan, 'files')
  }

  const onPickFolder = (list: FileList) => {
    // Folder selection NEVER starts by itself: review first (§17).
    setPending(scanFiles<File>(Array.from(list), limits))
  }

  const countsLine = (b: UploadBatchView) =>
    t('uploadBulk.progress', {
      done: b.counts.done + b.counts.failed + b.counts.cancelled,
      total: b.counts.total,
      success: b.counts.done,
      failed: b.counts.failed,
      remaining: b.counts.remaining,
    })

  const resultLine = (b: UploadBatchView): string | null => {
    if (b.finishedAt === null) return null
    if (b.counts.failed === 0 && b.counts.cancelled === 0) return t('uploadBulk.resultAllOk', { count: b.counts.done })
    if (b.counts.cancelled > 0) {
      return t('uploadBulk.resultCancelled', { ok: b.counts.done, rest: b.counts.cancelled + b.counts.failed })
    }
    return t('uploadBulk.resultPartial', { ok: b.counts.done, failed: b.counts.failed })
  }

  const failureText = (item: UploadBatchView['items'][number]): string => {
    switch (item.failureKind) {
      case 'network':
      case 'busy':
        return t(`uploadBulk.fail.${item.failureKind}`, { count: item.attempts })
      case 'auth':
        return t('uploadBulk.fail.auth')
      default:
        return item.error || t('uploadBulk.fail.rejected')
    }
  }

  const renderBatch = (b: UploadBatchView) => {
    const active = b.finishedAt === null
    const failedItems = b.items.filter((i) => i.status === 'failed')
    const inFlight = b.items.filter((i) => i.status === 'uploading' || i.status === 'finalizing').slice(0, 5)
    const result = resultLine(b)
    const showFailed = Boolean(expanded[b.id])
    return (
      <div className="iac-ub-batch" key={b.id} data-testid="iac-upload-batch" data-batch-id={b.id}>
        <div className="iac-ub-head">
          <span className="iac-ub-title">
            {b.source === 'folder' && b.label
              ? t('uploadBulk.batchTitleFolder', { name: b.label })
              : t('uploadBulk.batchTitleFiles', { count: b.counts.total })}
          </span>
          {b.placement.collectionName ? (
            <Tag className="iac-ub-target">{t('uploadBulk.target', { name: b.placement.collectionName })}</Tag>
          ) : null}
        </div>
        <Progress
          percent={b.counts.percent}
          size="small"
          status={active ? 'active' : b.counts.failed > 0 ? 'exception' : 'success'}
          data-testid="iac-upload-batch-progress"
        />
        <div className="iac-ub-counts" data-testid="iac-upload-batch-counts">
          {countsLine(b)}
        </div>
        {b.skippedCount > 0 ? (
          <div className="iac-ub-note">{t('uploadBulk.skippedNote', { count: b.skippedCount })}</div>
        ) : null}
        {result ? (
          <div className="iac-ub-result" data-testid="iac-upload-batch-result">
            {result}
          </div>
        ) : null}
        {b.haltedReason === 'auth' ? <Alert type="warning" showIcon message={t('uploadBulk.haltedAuth')} /> : null}
        {active && inFlight.length > 0 ? (
          <ul className="iac-ub-inflight">
            {inFlight.map((i) => (
              <li key={i.id} title={i.relativePath}>
                <span className="iac-ub-name">{i.name}</span>
                <span className="iac-ub-pct">{i.status === 'finalizing' ? '…' : `${i.progress}%`}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <Space size={8} wrap className="iac-ub-actions">
          {active ? (
            <Button size="small" onClick={() => imageUploadManager.cancelBatch(b.id)} data-testid="iac-upload-cancel">
              {t('uploadBulk.cancelUpload')}
            </Button>
          ) : null}
          {!active && failedItems.length > 0 ? (
            <Button
              size="small"
              type="primary"
              onClick={() => imageUploadManager.retryFailed(b.id)}
              data-testid="iac-upload-retry-failed"
            >
              {t('uploadBulk.retryFailed', { count: failedItems.length })}
            </Button>
          ) : null}
          {failedItems.length > 0 ? (
            <Button
              size="small"
              type="link"
              onClick={() => setExpanded((p) => ({ ...p, [b.id]: !p[b.id] }))}
              data-testid="iac-upload-toggle-failed"
            >
              {showFailed ? t('uploadBulk.hideFailed') : t('uploadBulk.showFailed')}
            </Button>
          ) : null}
          {!active ? (
            <Button size="small" type="text" onClick={() => imageUploadManager.dismissBatch(b.id)}>
              {t('uploadBulk.clearRecord')}
            </Button>
          ) : null}
        </Space>
        {showFailed && failedItems.length > 0 ? (
          <ul className="iac-ub-failed" data-testid="iac-upload-failed-list">
            {failedItems.slice(0, FAILED_ROWS_CAP).map((i) => (
              <li key={i.id} title={i.relativePath}>
                <CloseCircleFilled style={{ color: 'var(--ant-color-error, #ff4d4f)' }} />
                <span className="iac-ub-name">{i.name}</span>
                <span className="iac-ub-why">{failureText(i)}</span>
              </li>
            ))}
            {failedItems.length > FAILED_ROWS_CAP ? <li>… +{failedItems.length - FAILED_ROWS_CAP}</li> : null}
          </ul>
        ) : null}
      </div>
    )
  }

  const placementBlock = placement ? (
    <div className="upload-placement" data-testid="upload-placement">
      <label className="upload-placement-field">
        <span>{t('team.upload.saveTo')}</span>
        <Select
          value={collectionId || undefined}
          onChange={(v) => setCollectionId(v)}
          style={{ minWidth: 180 }}
          options={placement.collections.map((c) => ({ value: c.id, label: c.name }))}
          data-testid="upload-placement-collection"
          dropdownRender={(menu) => (
            <>
              {menu}
              {placement.canCreateCollection && (
                <div className="upload-placement-create" onMouseDown={(e) => e.stopPropagation()}>
                  <Input
                    size="small"
                    maxLength={60}
                    value={newCollectionName}
                    placeholder={t('team.upload.newCollectionPlaceholder')}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                  />
                  <Button
                    size="small"
                    type="link"
                    loading={creatingCollection}
                    disabled={!newCollectionName.trim()}
                    onClick={async () => {
                      setCreatingCollection(true)
                      try {
                        const created = await placement.onCreateCollection(newCollectionName.trim())
                        setCollectionId(created.id)
                        setNewCollectionName('')
                      } catch (e) {
                        message.error((e as Error).message)
                      } finally {
                        setCreatingCollection(false)
                      }
                    }}
                  >
                    {t('team.upload.newCollectionOk')}
                  </Button>
                </div>
              )}
            </>
          )}
        />
      </label>
      <label className="upload-placement-field">
        <span>{t('team.upload.visibility')}</span>
        <Select
          value={visibility}
          onChange={(v) => setVisibility(v)}
          style={{ minWidth: 140 }}
          options={[
            { value: 'workspace', label: t('team.share.shared') },
            { value: 'private', label: t('team.share.private') },
          ]}
          data-testid="upload-placement-visibility"
        />
      </label>
      {visibility === 'private' && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('team.upload.visibilityHintPrivate')}
        </Typography.Text>
      )}
    </div>
  ) : null

  const activeAny = batches.some((b) => b.finishedAt === null)

  const reviewPanel = pending ? (
    <div className="iac-ub-review" data-testid="iac-upload-review">
      <h3 className="iac-ub-review-title">{t('uploadBulk.reviewTitle')}</h3>
      <dl className="iac-ub-facts">
        <dt>{t('uploadBulk.sourceFolder')}</dt>
        <dd data-testid="iac-upload-review-folder">{pending.folderName ?? '—'}</dd>
        <dt>{t('uploadBulk.scanned')}</dt>
        <dd>{pending.scannedCount}</dd>
        <dt>{t('uploadBulk.valid')}</dt>
        <dd data-testid="iac-upload-review-valid">{pending.valid.length}</dd>
        <dt>{t('uploadBulk.skippedLabel')}</dt>
        <dd data-testid="iac-upload-review-skipped">{pending.skipped.length}</dd>
        <dt>{t('uploadBulk.totalSize')}</dt>
        <dd data-testid="iac-upload-review-size">
          {formatBytes(pending.totalBytes)} / {maxTotalLabel}
        </dd>
        <dt>{t('uploadBulk.targetLabel')}</dt>
        <dd data-testid="iac-upload-review-target">{targetName || '—'}</dd>
      </dl>
      {pending.skipped.length > 0 ? (
        <div className="iac-ub-skipped">
          {(Object.keys(pending.skippedByReason) as SkipReason[])
            .filter((r) => pending.skippedByReason[r] > 0)
            .map((r) => (
              <Tag key={r}>
                {r === 'oversize'
                  ? `${t(`uploadBulk.skipReason.${r}`)} (${maxFileLabel}) × ${pending.skippedByReason[r]}`
                  : `${t(`uploadBulk.skipReason.${r}`)} × ${pending.skippedByReason[r]}`}
              </Tag>
            ))}
          <details>
            <summary>{t('uploadBulk.skippedDetails')}</summary>
            <ul className="iac-ub-failed">
              {pending.skipped.slice(0, 100).map((s, idx) => (
                <li key={`${s.relativePath}-${idx}`} title={s.relativePath}>
                  <span className="iac-ub-name">{s.relativePath}</span>
                  <span className="iac-ub-why">{t(`uploadBulk.skipReason.${s.reason}`)}</span>
                </li>
              ))}
              {pending.skipped.length > 100 ? <li>… +{pending.skipped.length - 100}</li> : null}
            </ul>
          </details>
        </div>
      ) : null}
      {pending.blocked ? (
        <Alert
          type="error"
          showIcon
          data-testid="iac-upload-review-error"
          message={limitErrorText(pending).map((line) => (
            <div key={line}>{line}</div>
          ))}
        />
      ) : (
        <Typography.Text type="secondary">
          {t('uploadBulk.statusWaiting')} · {t('uploadBulk.willUpload', { count: pending.valid.length })}
        </Typography.Text>
      )}
      <Space className="iac-ub-review-actions">
        <Button onClick={() => setPending(null)} data-testid="iac-upload-review-cancel">
          {t('uploadBulk.reselect')}
        </Button>
        <Button
          type="primary"
          disabled={pending.blocked}
          onClick={() => startBatch(pending, 'folder')}
          data-testid="iac-upload-review-start"
        >
          {t('uploadBulk.start')}
        </Button>
      </Space>
    </div>
  ) : null

  const selectPanel = (
    <>
      <Typography.Paragraph type="secondary">{t('uploadBulk.description')}</Typography.Paragraph>
      <Space wrap className="iac-ub-pick">
        <Button
          icon={<PictureOutlined />}
          onClick={() => filesInputRef.current?.click()}
          data-testid="iac-upload-pick-images"
        >
          {t('uploadBulk.pickImages')}
        </Button>
        <Tooltip title={folderSupported ? undefined : t('uploadBulk.folderUnsupported')}>
          <span>
            <Button
              icon={<FolderOpenOutlined />}
              disabled={!folderSupported}
              onClick={() => folderInputRef.current?.click()}
              data-testid="iac-upload-pick-folder"
            >
              {t('uploadBulk.pickFolder')}
            </Button>
          </span>
        </Tooltip>
      </Space>
      {!folderSupported ? (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6 }}>
          {t('uploadBulk.folderUnsupported')}
        </Typography.Paragraph>
      ) : null}
      <div
        className={`folder-picker-zone${dragOver ? ' folder-picker-zone-dragover' : ''}`}
        onClick={() => filesInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          if (e.dataTransfer.files?.length) onPickFiles(e.dataTransfer.files)
        }}
        role="button"
        tabIndex={0}
        style={{ marginTop: 12 }}
      >
        <Space direction="vertical" size={6}>
          <InboxOutlined style={{ fontSize: 26, color: 'var(--accent)' }} />
          <span>{t('uploadBulk.dropHint')}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('uploadBulk.limitsHint', {
              types: typesLabel,
              maxFile: maxFileLabel,
              maxFiles: limits.maxFiles,
              maxTotal: maxTotalLabel,
            })}
          </Typography.Text>
        </Space>
      </div>
    </>
  )

  return (
    <Modal
      title={title ?? t('uploadModalTitle')}
      open={open}
      onCancel={onClose}
      width={680}
      destroyOnClose
      footer={
        <Space style={{ width: '100%', justifyContent: 'flex-end', display: 'flex' }}>
          <Button type="primary" onClick={onClose} data-testid="iac-upload-close">
            {activeAny ? t('uploadBulk.closeBackground') : t('uploadBulk.done')}
          </Button>
        </Space>
      }
    >
      {placementBlock}
      {pending ? reviewPanel : selectPanel}

      {/* hidden native pickers: multi-file + whole-folder (recursive) */}
      <input
        ref={filesInputRef}
        type="file"
        multiple
        accept={[...limits.allowedMime].join(',')}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files?.length) onPickFiles(e.target.files)
          e.target.value = ''
        }}
        data-testid="upload-file-input"
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files?.length) onPickFolder(e.target.files)
          e.target.value = ''
        }}
        data-testid="upload-folder-input"
      />

      {batches.length > 0 ? (
        <div className="iac-ub-batches" data-testid="iac-upload-batches">
          <h3 className="iac-ub-section">{t('uploadBulk.tasks')}</h3>
          {[...batches].reverse().map(renderBatch)}
        </div>
      ) : null}
    </Modal>
  )
}
